// CookieConsent.js — Banner de cookies + panel de preferencias por proyecto.
//
// Se configura en Admin → Proyecto → Cookies (config_json.consentimiento) y está
// apagado salvo que el proyecto lo active. Mientras el visitante no decida, las
// categorías opcionales quedan denegadas y ProjectContext no inyecta los scripts
// de terceros de esas categorías.
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useProject } from '../contexts/ProjectContext';
import {
  CATEGORIAS,
  EVENTO_ABRIR,
  aceptarTodas,
  rechazarTodas,
  guardarConsentimiento,
  leerConsentimiento,
} from '../utils/consentimiento';

const Barra = styled.div`
  position: fixed;
  z-index: 3000;
  left: ${p => (p.$pos === 'abajo-izquierda' ? '18px' : p.$pos === 'abajo-derecha' ? 'auto' : '0')};
  right: ${p => (p.$pos === 'abajo-derecha' ? '18px' : p.$pos === 'abajo-izquierda' ? 'auto' : '0')};
  bottom: ${p => (p.$pos === 'barra' ? '0' : '18px')};
  width: ${p => (p.$pos === 'barra' ? '100%' : 'min(420px, calc(100vw - 36px))')};
  background: ${p => p.$bg || '#ffffff'};
  color: ${p => p.$color || '#1f2937'};
  border-radius: ${p => (p.$pos === 'barra' ? '0' : (p.$radio || '12px'))};
  box-shadow: ${p => p.$sombra || '0 8px 30px rgba(0,0,0,0.22)'};
  padding: ${p => p.$padding || '1.1rem 1.3rem'};
  font-size: 0.9rem;
  line-height: 1.45;
  display: flex;
  flex-direction: ${p => (p.$pos === 'barra' ? 'row' : 'column')};
  align-items: ${p => (p.$pos === 'barra' ? 'center' : 'stretch')};
  gap: ${p => (p.$pos === 'barra' ? '1.2rem' : '0.9rem')};
  @media (max-width: 768px) {
    left: 0; right: 0; bottom: 0; width: 100%;
    border-radius: ${p => (p.$pos === 'barra' ? '0' : '12px 12px 0 0')};
    flex-direction: column; align-items: stretch;
  }
`;

const Titulo = styled.div`
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.2rem;
`;

const Texto = styled.div`
  flex: 1;
  a { color: inherit; text-decoration: underline; }
`;

const Botones = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex-shrink: 0;
  @media (max-width: 768px) { & > button { flex: 1 1 auto; } }
`;

const Boton = styled.button`
  border: ${p => (p.$secundario ? `1px solid ${p.$color || '#94a3b8'}` : 'none')};
  background: ${p => (p.$secundario ? 'transparent' : (p.$bg || '#0f355b'))};
  color: ${p => (p.$secundario ? (p.$color || '#334155') : (p.$colorTexto || '#ffffff'))};
  border-radius: ${p => p.$radio || '8px'};
  padding: 0.55rem 1.1rem;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: filter 0.15s, transform 0.15s;
  &:hover { filter: brightness(1.08); transform: translateY(-1px); }
`;

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 3100;
  background: rgba(2,6,23,0.55);
  display: flex; align-items: center; justify-content: center;
  padding: 20px; overflow-y: auto;
`;

const Panel = styled.div`
  background: ${p => p.$bg || '#ffffff'};
  color: ${p => p.$color || '#1f2937'};
  width: 100%; max-width: 560px; margin: auto;
  border-radius: ${p => p.$radio || '12px'};
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  padding: 1.5rem 1.5rem 1.2rem;
`;

/* Cada categoría es una fila tipo acordeón: nombre a la izquierda, estado a la
   derecha y la explicación se despliega con la flecha. */
const Fila = styled.div`
  background: ${p => p.$bg || '#f3f4f6'};
  border-radius: 6px;
  margin-bottom: 0.45rem;
  overflow: hidden;
`;

const FilaCab = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
`;

const FilaNombre = styled.div`
  flex: 1;
  font-size: 0.95rem;
  font-weight: 500;
`;

const SiempreActivo = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  color: ${p => p.$color || '#16a34a'};
`;

const Chevron = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  opacity: 0.65;
  padding: 0 0 0 0.35rem;
  display: inline-flex;
  transition: transform 0.18s;
  transform: rotate(${p => (p.$abierto ? '180deg' : '0deg')});
  &:hover { opacity: 1; }
`;

const FilaDesc = styled.div`
  padding: 0 1rem 0.9rem;
  font-size: 0.82rem;
  line-height: 1.45;
  opacity: 0.8;
`;

const BotonesPanel = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.6rem;
  margin-top: 1.1rem;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const EnlaceLegal = styled.a`
  display: block;
  text-align: center;
  margin-top: 1rem;
  font-size: 0.85rem;
  color: ${p => p.$color || '#2563eb'};
  text-decoration: underline;
`;

const Cerrar = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.5rem;
  line-height: 1;
  color: inherit;
  opacity: 0.7;
  &:hover { opacity: 1; }
`;

/* Pestaña pegada al borde inferior (esquinas superiores redondeadas), como la
   de los gestores de consentimiento habituales. Lado configurable. */
const Pestana = styled.button`
  position: fixed;
  z-index: 2900;
  bottom: 0;
  ${p => (p.$lado === 'centro'
    ? 'left: 50%; transform: translateX(-50%);'
    : p.$lado === 'derecha' ? 'right: 24px;' : 'left: 24px;')}
  border: none;
  cursor: pointer;
  background: ${p => p.$bg || '#ffffff'};
  color: ${p => p.$color || '#374151'};
  border-radius: ${p => p.$radio || '14px 14px 0 0'};
  padding: ${p => p.$padding || '0.7rem 1.4rem'};
  font-size: ${p => p.$size || '0.9rem'};
  font-weight: 500;
  font-family: inherit;
  box-shadow: 0 -2px 14px rgba(0,0,0,0.16);
  transition: padding 0.15s;
  &:hover { padding-bottom: 1rem; }
  @media (max-width: 480px) { font-size: 0.82rem; padding: 0.6rem 1rem; }
`;

const CookieConsent = () => {
  const { proyecto } = useProject();
  const cfg = proyecto?.config_json?.consentimiento || {};
  const activo = cfg.activo === true;

  const [decision, setDecision] = useState(null);
  const [listo, setListo] = useState(false);
  const [panel, setPanel] = useState(false);
  const [sel, setSel] = useState(() => aceptarTodas());
  const [abiertas, setAbiertas] = useState({});   // acordeón por categoría

  // Decisión guardada (o null si nunca decidió / caducó / cambió la versión)
  useEffect(() => {
    if (!activo || !proyecto?.id) { setListo(false); return; }
    const dec = leerConsentimiento(proyecto.id, cfg.version, cfg.caducidad_meses);
    setDecision(dec);
    if (dec?.categorias) setSel(dec.categorias);
    setListo(true);
  }, [activo, proyecto, cfg.version, cfg.caducidad_meses]);

  // Reabrir preferencias desde fuera: evento global o enlaces a #consentimiento
  const abrir = useCallback(() => setPanel(true), []);
  useEffect(() => {
    if (!activo) return undefined;
    const porEnlace = (e) => {
      const a = e.target?.closest?.('a[href="#consentimiento"], [data-consentimiento="abrir"]');
      if (!a) return;
      e.preventDefault();
      abrir();
    };
    window.addEventListener(EVENTO_ABRIR, abrir);
    document.addEventListener('click', porEnlace);
    return () => {
      window.removeEventListener(EVENTO_ABRIR, abrir);
      document.removeEventListener('click', porEnlace);
    };
  }, [activo, abrir]);

  if (!activo || !listo) return null;

  const guardar = (categorias) => {
    setDecision(guardarConsentimiento(proyecto.id, cfg.version, categorias));
    setPanel(false);
  };

  const estilos = {
    $bg: cfg.color_fondo,
    $color: cfg.color_texto,
    $radio: cfg.radio,
  };
  const botonPrimario = { $bg: cfg.color_boton, $colorTexto: cfg.color_texto_boton, $radio: cfg.radio_boton };
  const botonSecundario = { $secundario: true, $color: cfg.color_boton_secundario || cfg.color_texto, $radio: cfg.radio_boton };

  const textoBanner = cfg.texto
    || 'Usamos cookies propias y de terceros para que el sitio funcione, medir su uso y mejorar tu experiencia. Puedes aceptarlas todas, rechazar las opcionales o elegir cuáles permites.';

  return (
    <>
      {!decision && (
        <Barra $pos={cfg.posicion || 'abajo-izquierda'} $padding={cfg.padding} $sombra={cfg.sombra} {...estilos}>
          <Texto>
            {cfg.titulo !== '' && <Titulo>{cfg.titulo || 'Tu privacidad'}</Titulo>}
            <span dangerouslySetInnerHTML={{ __html: textoBanner }} />
            {cfg.enlace_politica && (
              <>
                {' '}
                <a href={cfg.enlace_politica} target={cfg.enlace_nueva_pestana === false ? undefined : '_blank'} rel="noopener noreferrer">
                  {cfg.texto_politica || 'Más información'}
                </a>
              </>
            )}
          </Texto>
          <Botones>
            <Boton {...botonPrimario} onClick={() => guardar(aceptarTodas())}>
              {cfg.texto_aceptar || 'Aceptar todas'}
            </Boton>
            {cfg.mostrar_rechazar !== false && (
              <Boton {...botonSecundario} onClick={() => guardar(rechazarTodas())}>
                {cfg.texto_rechazar || 'Rechazar opcionales'}
              </Boton>
            )}
            <Boton {...botonSecundario} onClick={abrir}>
              {cfg.texto_preferencias || 'Preferencias'}
            </Boton>
          </Botones>
        </Barra>
      )}

      {decision && cfg.mostrar_flotante !== false && (
        <Pestana
          $lado={cfg.flotante_lado}
          $bg={cfg.pestana_fondo || cfg.color_fondo}
          $color={cfg.pestana_color || cfg.color_texto}
          $radio={cfg.pestana_radio}
          $padding={cfg.pestana_padding}
          $size={cfg.pestana_tamano}
          onClick={abrir}
        >
          {cfg.texto_gestionar || 'Gestionar consentimiento'}
        </Pestana>
      )}

      {panel && (
        <Overlay onClick={(e) => { if (e.target === e.currentTarget) setPanel(false); }}>
          <Panel {...estilos}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.9rem' }}>
              <Titulo style={{ flex: 1, textAlign: 'center', margin: 0, fontSize: '1.1rem' }}>
                {cfg.titulo_panel || 'Gestionar consentimiento'}
              </Titulo>
              <Cerrar type="button" aria-label="Cerrar" onClick={() => setPanel(false)}>×</Cerrar>
            </div>

            <div style={{ fontSize: '0.86rem', opacity: 0.85, lineHeight: 1.5, marginBottom: '1.1rem' }}>
              {cfg.texto_panel
                || 'Para ofrecer las mejores experiencias utilizamos tecnologías como las cookies para almacenar y/o acceder a la información del dispositivo. No consentir o retirar el consentimiento puede afectar negativamente a ciertas características y funciones.'}
            </div>

            {CATEGORIAS.map(cat => {
              const abiertoCat = !!abiertas[cat.key];
              return (
                <Fila key={cat.key} $bg={cfg.color_fila}>
                  <FilaCab>
                    <FilaNombre>{(cfg.etiquetas || {})[cat.key] || cat.label}</FilaNombre>
                    {cat.fija ? (
                      <SiempreActivo $color={cfg.color_siempre_activo}>
                        {cfg.texto_siempre_activo || 'Siempre activo'}
                      </SiempreActivo>
                    ) : (
                      <input
                        type="checkbox"
                        aria-label={cat.label}
                        checked={!!sel[cat.key]}
                        onChange={e => setSel(prev => ({ ...prev, [cat.key]: e.target.checked }))}
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                      />
                    )}
                    <Chevron
                      type="button"
                      $abierto={abiertoCat}
                      aria-label={abiertoCat ? 'Ocultar detalle' : 'Ver detalle'}
                      onClick={() => setAbiertas(prev => ({ ...prev, [cat.key]: !prev[cat.key] }))}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Chevron>
                  </FilaCab>
                  {abiertoCat && (
                    <FilaDesc>{(cfg.descripciones || {})[cat.key] || cat.desc}</FilaDesc>
                  )}
                </Fila>
              );
            })}

            <BotonesPanel>
              <Boton {...botonPrimario} onClick={() => guardar(aceptarTodas())}>
                {cfg.texto_aceptar_panel || 'Aceptar'}
              </Boton>
              <Boton {...botonSecundario} onClick={() => guardar(rechazarTodas())}>
                {cfg.texto_denegar || 'Denegar'}
              </Boton>
              <Boton {...botonSecundario} onClick={() => guardar(sel)}>
                {cfg.texto_guardar || 'Guardar preferencias'}
              </Boton>
            </BotonesPanel>

            {cfg.enlace_politica && (
              <EnlaceLegal
                href={cfg.enlace_politica}
                target={cfg.enlace_nueva_pestana === false ? undefined : '_blank'}
                rel="noopener noreferrer"
                $color={cfg.color_enlace}
              >
                {cfg.texto_politica || 'Aviso legal'}
              </EnlaceLegal>
            )}
          </Panel>
        </Overlay>
      )}
    </>
  );
};

export default CookieConsent;
